import { useState, useEffect } from 'react';

import { mediaIdOf } from 'src/utils/media';

import { getMediaContentBlob } from 'src/services/media/media-content-service-calls';

// ----------------------------------------------------------------------
// Convierte un documento del backend en una URL `blob:` utilizable en <img src>,
// <iframe src> o <embed src>.
//
// Por que no se pone la URL del endpoint directamente en el src: la sesion de esta
// aplicacion es un JWT en sessionStorage que axios manda en la cabecera Authorization
// (ver src/auth/context/jwt/utils.js). Una peticion que dispara el navegador por su
// cuenta (<img src>) NO lleva esa cabecera, asi que el backend responderia 401 y la
// imagen saldria rota. Se pide con axios (autenticado) y se envuelve el binario en una
// object URL. Sigue siendo el backend quien sirve el archivo y no hay base64 de por medio.
// ----------------------------------------------------------------------

/**
 * @param {string|object|null} media mediaId o el objeto MediaBlob/MediaResponseDto.
 * @param {object} [opts]
 * @param {string} [opts.patientId] Ancla la descarga al paciente dueno del documento.
 * @param {boolean} [opts.inline] Pide el documento para visor en vez de descarga.
 * @param {boolean} [opts.enabled=true] Permite diferir la descarga (p. ej. hasta abrir un dialogo).
 * @returns {{ url: string|null, loading: boolean, error: boolean }}
 *   `error` es true cuando el backend respondio 404 (no existe, borrado, sin permiso o
 *   sin pertenencia): la vista debe pintar un placeholder, no una imagen rota.
 */
export function useMediaObjectUrl(media, { patientId, inline = false, enabled = true } = {}) {
  const mediaId = mediaIdOf(media);

  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!mediaId || !enabled) {
      setUrl(null);
      setLoading(false);
      setError(false);
      return undefined;
    }

    let cancelado = false;
    let objectUrl = null;
    const controller = new AbortController();

    setLoading(true);
    setError(false);

    getMediaContentBlob(mediaId, { patientId, inline, signal: controller.signal })
      .then((blob) => {
        if (cancelado) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => {
        if (cancelado) return;
        setUrl(null);
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelado = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId, patientId, inline, enabled]);

  return { url, loading, error };
}

export default useMediaObjectUrl;
